import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { usersApi, bereavementsApi, areasApi, announcementsApi, contributionsApi, directoryApi, statementsApi, treasuryApi, settingsApi, adminApi, financeApi, adminToolsApi } from "@/lib/api";
import { downloadBlob, todayStr } from "@/lib/download";
import { readImageAsDataUrl } from "@/lib/image";
import { visibleAdminTabs, defaultAdminTab, roleLabel, roleColor, isSuperAdmin } from "@/lib/roles";
import { bereavementSubtitle } from "@/lib/bereavement";
import AdminKpiDrillDown from "./components/AdminKpiDrillDown";
import { ViewToggle } from "@/components/ViewToggle";
import NotificationsBell from "@/components/NotificationsBell";
import { Sidebar } from "@/components/Sidebar";
import BankingTab from "./components/BankingTab";
import PaymentsTab from "./components/PaymentsTab";
import RemindersTab from "./components/RemindersTab";
import WalletsAdminTab from "./components/WalletsAdminTab";
import { Heart, SignOut, UserCircle, Plus, Trash, Check, X, MapPin, MagnifyingGlass, CaretRight, CalendarDots, Receipt, ChartBar, Gear, Bell, WarningCircle, Skull, UserMinus, ArrowRight, CurrencyDollar, ClockCountdown, CheckCircle, NotePencil, ClipboardText, UserSwitch, Megaphone, UsersThree, House, Bank, DownloadSimple, PaperPlaneTilt, Wallet } from "@phosphor-icons/react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  probation: "bg-purple-100 text-purple-800",
  active: "bg-green-100 text-green-800",
  deceased: "bg-gray-200 text-gray-600",
  exited: "bg-orange-100 text-orange-700",
  suspended: "bg-red-100 text-red-800",
};

const RELATIONSHIPS = ["Member", "Spouse", "Child", "Parent", "Sibling", "Other"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("pending");
  const [kpiTopic, setKpiTopic] = useState(null);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [probationMembers, setProbationMembers] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [bereavements, setBereavements] = useState([]);
  const [areas, setAreas] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [settings, setSettings] = useState({ registrationFee: 100, probationMonths: 3, contributionAmount: 200 });
  const [loading, setLoading] = useState(true);
  const [newArea, setNewArea] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  // Bereavement form
  const [showCreateB, setShowCreateB] = useState(false);
  const [bForm, setBForm] = useState({ deceasedName: "", relationship: "", memberId: "", description: "", dateOfDeath: "", contributionAmount: "", imageUrl: "" });
  const [bSubmitting, setBSubmitting] = useState(false);
  const [bImageProcessing, setBImageProcessing] = useState(false);
  // Edit obituary
  const [editBereavement, setEditBereavement] = useState(null);
  const [eForm, setEForm] = useState({ deceasedName: "", relationship: "", description: "", dateOfDeath: "", contributionAmount: "", imageUrl: "" });
  const [eSubmitting, setESubmitting] = useState(false);
  const [eImageProcessing, setEImageProcessing] = useState(false);
  // Announcement
  const [showCreateA, setShowCreateA] = useState(false);
  const [aForm, setAForm] = useState({ title: "", body: "", priority: "normal" });
  // Add member
  const [showAddMember, setShowAddMember] = useState(false);
  const [mForm, setMForm] = useState({ name: "", phone: "", clanName: "", familyName: "", nextOfKinName: "", nextOfKinPhone: "", status: "active", registrationDate: "" });
  // Sub-views
  const [memberStatementId, setMemberStatementId] = useState(null);
  const [memberStatement, setMemberStatement] = useState(null);
  const [stmtSort, setStmtSort] = useState("recentPaid");
  const [selectedBereavementId, setSelectedBereavementId] = useState(null);
  const [bereavementDetail, setBereavementDetail] = useState(null);
  const [arrearsMembers, setArrearsMembers] = useState([]);
  // Remarks
  const [remarksFor, setRemarksFor] = useState(null);
  const [editMember, setEditMember] = useState(null); // member object for editing
  const [remarksText, setRemarksText] = useState("");
  // Exit dialog
  const [exitFor, setExitFor] = useState(null);
  const [exitType, setExitType] = useState("voluntary");
  const [exitRemarks, setExitRemarks] = useState("");
  // Settings edit
  const [editSettings, setEditSettings] = useState(false);
  const [sForm, setSForm] = useState({});
  // Mass Import
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // Email config
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  // Finance overview (for KPI cards)
  const [finance, setFinance] = useState(null);
  // Team & Roles
  const [teamMembers, setTeamMembers] = useState([]);
  const [memberSearchQ, setMemberSearchQ] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [pm, appr, prob, am, all, b, a, ann, s] = await Promise.all([
        usersApi.getMembers("pending"), usersApi.getMembers("approved"), usersApi.getMembers("probation"),
        usersApi.getMembers("active"), usersApi.getMembers(), bereavementsApi.list(), areasApi.list(),
        announcementsApi.list(), settingsApi.get()
      ]);
      setPendingMembers(pm.data); setApprovedMembers(appr.data); setProbationMembers(prob.data);
      setActiveMembers(am.data); setAllMembers(all.data); setBereavements(b.data); setAreas(a.data);
      setAnnouncements(ann.data); setSettings(s.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (tab === "reports") treasuryApi.summary().then(({ data }) => setTreasury(data)).catch(() => {}); }, [tab]);
  useEffect(() => {
    // Load finance overview for KPI cards (super_admin / admin / treasurer have access)
    if (user?.role && ["super_admin", "admin", "treasurer"].includes(user.role)) {
      financeApi.overview().then(({ data }) => setFinance(data)).catch(() => setFinance(null));
    }
  }, [user?.role]);
  useEffect(() => { if (tab === "settings") adminApi.emailStatus().then(({ data }) => setEmailStatus(data)).catch(() => setEmailStatus(null)); }, [tab]);
  useEffect(() => { if (tab === "team") adminApi.listAdminUsers().then(({ data }) => setTeamMembers(data)).catch(() => setTeamMembers([])); }, [tab]);

  const [csvBusy, setCsvBusy] = useState(null);
  const handleCsvExport = async (kind) => {
    setCsvBusy(kind);
    try {
      const { data } = kind === "members"
        ? await adminApi.exportMembersCsv()
        : await adminApi.exportContributionsCsv();
      downloadBlob(data, `utheri-${kind}-${todayStr()}.csv`);
      toast.success(`${kind === "members" ? "Members" : "Contributions"} CSV downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setCsvBusy(null);
    }
  };

  // ─── Actions ───────────────────────────────────────────────────────────────
  const handleApprove = async (id) => { try { const { data } = await usersApi.approveMember(id); toast.success(`Approved! UKC: ${data.ukcNumber}`); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handlePayRegistration = async (id) => { try { await usersApi.payRegistration(id); toast.success("Registration fee recorded"); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleStartProbation = async (id) => { try { const { data } = await usersApi.startProbation(id); toast.success(data.message); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleActivate = async (id) => { try { await usersApi.activateMember(id); toast.success("Member activated!"); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleExit = async () => { if (!exitFor) return; try { await usersApi.exitMember(exitFor, { exitType, remarks: exitRemarks }); toast.success(`Member marked as ${exitType === "deceased" ? "deceased" : "exited"}`); setExitFor(null); setExitRemarks(""); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleSaveRemarks = async () => { if (!remarksFor) return; try { await usersApi.updateRemarks(remarksFor, remarksText); toast.success("Remarks saved"); setRemarksFor(null); fetchData(); } catch { toast.error("Failed"); } };
  const handleSuspend = async (id) => { try { await usersApi.updateStatus(id, "suspended"); toast.success("Suspended"); fetchData(); } catch { toast.error("Failed"); } };
  const handleAddArea = async () => { if (!newArea.trim()) return; try { await areasApi.add(newArea.trim()); setNewArea(""); toast.success("Area added"); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleDeleteArea = async (id) => { try { await areasApi.remove(id); toast.success("Removed"); fetchData(); } catch { toast.error("Failed"); } };
  const handleCreateBereavement = async () => {
    if (!bForm.deceasedName || !bForm.relationship || !bForm.memberId || !bForm.dateOfDeath) { toast.error("Fill required fields"); return; }
    setBSubmitting(true);
    try { await bereavementsApi.create({ ...bForm, contributionAmount: Number(bForm.contributionAmount) || undefined, imageUrl: bForm.imageUrl || null }); toast.success("Posted!"); setShowCreateB(false); setBForm({ deceasedName: "", relationship: "", memberId: "", description: "", dateOfDeath: "", contributionAmount: "", imageUrl: "" }); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBSubmitting(false); }
  };
  const handleBImageChange = async (file) => {
    if (!file) return;
    setBImageProcessing(true);
    try { const dataUrl = await readImageAsDataUrl(file); setBForm(f => ({ ...f, imageUrl: dataUrl })); }
    catch (e) { toast.error(e?.message || "Could not read image"); }
    finally { setBImageProcessing(false); }
  };
  const openEditBereavement = (b) => {
    setEditBereavement(b);
    setEForm({
      deceasedName: b.deceasedName || "",
      relationship: b.relationship || "",
      description: b.description || "",
      dateOfDeath: b.dateOfDeath ? String(b.dateOfDeath).slice(0, 10) : "",
      contributionAmount: b.contributionAmount || "",
      imageUrl: b.imageUrl || "",
    });
  };
  const handleEditBereavement = async () => {
    if (!editBereavement) return;
    if (!eForm.deceasedName || !eForm.relationship || !eForm.dateOfDeath) { toast.error("Fill required fields"); return; }
    setESubmitting(true);
    try {
      await bereavementsApi.update(editBereavement.id, {
        deceasedName: eForm.deceasedName,
        relationship: eForm.relationship,
        description: eForm.description,
        dateOfDeath: eForm.dateOfDeath,
        contributionAmount: Number(eForm.contributionAmount) || undefined,
        imageUrl: eForm.imageUrl || null,
      });
      toast.success("Obituary updated");
      setEditBereavement(null);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setESubmitting(false); }
  };
  const handleEImageChange = async (file) => {
    if (!file) return;
    setEImageProcessing(true);
    try { const dataUrl = await readImageAsDataUrl(file); setEForm(f => ({ ...f, imageUrl: dataUrl })); }
    catch (e) { toast.error(e?.message || "Could not read image"); }
    finally { setEImageProcessing(false); }
  };
  const handleCloseBereavement = async (id) => { try { await bereavementsApi.close(id); toast.success("Closed"); fetchData(); setSelectedBereavementId(null); } catch { toast.error("Failed"); } };
  const handleCreateAnnouncement = async () => { if (!aForm.title || !aForm.body) { toast.error("Fill fields"); return; } try { await announcementsApi.create(aForm); toast.success("Posted!"); setShowCreateA(false); setAForm({ title: "", body: "", priority: "normal" }); fetchData(); } catch { toast.error("Failed"); } };
  const handleDeleteAnnouncement = async (id) => { try { await announcementsApi.remove(id); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); } };
  const handleAddMember = async () => { if (!mForm.name || !mForm.phone) { toast.error("Name and phone required"); return; } try { await usersApi.addMember(mForm); toast.success("Added!"); setShowAddMember(false); setMForm({ name: "", phone: "", clanName: "", familyName: "", nextOfKinName: "", nextOfKinPhone: "", status: "active", registrationDate: "" }); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const handleSaveSettings = async () => { try { const { data } = await settingsApi.update(sForm); setSettings(data); toast.success("Settings saved"); setEditSettings(false); } catch { toast.error("Failed"); } };
  const handleLoadPreview = async () => { try { const { data } = await usersApi.registerPreview(); setImportPreview(data); } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load register file"); } };
  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await adminApi.emailTest(testEmailTo);
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email test failed");
    } finally {
      setSendingTest(false);
    }
  };
  const handleSearchMembers = async (q) => {
    setMemberSearchQ(q);
    if (!q || q.trim().length < 2) { setMemberSearchResults([]); return; }
    try {
      const { data } = await adminApi.searchMembers(q.trim());
      setMemberSearchResults(data);
    } catch { setMemberSearchResults([]); }
  };
  const handleSetTeamRole = async (userId, role) => {
    try {
      await adminApi.setRole(userId, role);
      toast.success(`Role updated to ${role}`);
      const { data } = await adminApi.listAdminUsers();
      setTeamMembers(data);
      if (memberSearchQ) await handleSearchMembers(memberSearchQ);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change role");
    }
  };
  const handleRunImport = async (reset) => {
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await usersApi.importRegister({ reset });
      setImportResult(data);
      toast.success(`Imported ${data.imported} members, ${data.contributionsRecorded} contributions`);
      setConfirmReset(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };
  const handleRecordContribution = async (memberId) => { if (!selectedBereavementId) return; try { await contributionsApi.record({ bereavementId: selectedBereavementId, memberId }); toast.success("Recorded!"); bereavementsApi.get(selectedBereavementId).then(({ data }) => setBereavementDetail(data)); directoryApi.arrears(selectedBereavementId).then(({ data }) => setArrearsMembers(data)); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  useEffect(() => { setStmtSort("recentPaid"); }, [memberStatementId]);
  useEffect(() => { if (memberStatementId) statementsApi.forMember(memberStatementId).then(({ data }) => setMemberStatement(data)).catch(() => setMemberStatement(null)); else setMemberStatement(null); }, [memberStatementId]);
  useEffect(() => { if (selectedBereavementId) { bereavementsApi.get(selectedBereavementId).then(({ data }) => setBereavementDetail(data)).catch(() => {}); directoryApi.arrears(selectedBereavementId).then(({ data }) => setArrearsMembers(data)).catch(() => {}); } else { setBereavementDetail(null); setArrearsMembers([]); } }, [selectedBereavementId]);

  const allMembersForTab = allMembers.filter(m => m.role !== "admin" || m.status === "active");
  const filteredPending = pendingSearch.trim() ? pendingMembers.filter(m => m.name?.toLowerCase().includes(pendingSearch.toLowerCase()) || m.phone?.includes(pendingSearch)) : pendingMembers;
  const filteredMembers = memberSearch.trim() ? allMembers.filter(m => m.name?.toLowerCase().includes(memberSearch.toLowerCase()) || m.phone?.includes(memberSearch) || m.memberNumber?.toLowerCase().includes(memberSearch.toLowerCase()) || m.ukcNumber?.toLowerCase().includes(memberSearch.toLowerCase())) : allMembers;

  const allAdminTabs = [
    { key: "pending", label: "Pending", count: pendingMembers.length, icon: ClockCountdown },
    { key: "workflow", label: "Workflow", count: approvedMembers.length + probationMembers.length, icon: UserSwitch },
    { key: "members", label: "All Members", count: allMembers.length, icon: UsersThree },
    { key: "bereavements", label: "Obituaries", count: bereavements.filter(b => b.status === "active").length, icon: Heart },
    { key: "areas", label: "Areas", icon: MapPin },
    { key: "announcements", label: "Notices", icon: Megaphone },
    { key: "banking", label: "Banking", icon: Bank },
    { key: "wallets", label: "Wallets", icon: Wallet },
    { key: "payments", label: "Payments", icon: Receipt },
    { key: "reminders", label: "Reminders", icon: PaperPlaneTilt },
    { key: "reports", label: "Reports", icon: ChartBar },
    { key: "team", label: "Team & Roles", icon: ClipboardText },
    { key: "settings", label: "Settings", icon: Gear },
  ];
  const visibleKeys = visibleAdminTabs(user?.role || "member");
  const adminTabs = allAdminTabs.filter(t => visibleKeys.includes(t.key));
  // Ensure active tab is within visible tabs (e.g., treasurer default to 'reports')
  useEffect(() => {
    if (adminTabs.length && !adminTabs.some(t => t.key === tab)) {
      setTab(defaultAdminTab(user?.role) || adminTabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // ─── Sub-views ─────────────────────────────────────────────────────────────
  if (memberStatementId && memberStatement) {
    const downloadPdf = async () => {
      try {
        const res = await statementsApi.memberPdfBlob(memberStatementId, stmtSort);
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url; a.download = `${memberStatement.member.name}-statement.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch { toast.error("Failed to download statement"); }
    };
    const viewPdf = () => window.open(statementsApi.memberPdfUrl(memberStatementId, stmtSort), "_blank");
    const recent = stmtSort === "recentPaid";
    const stmtRows = recent
      ? [...memberStatement.rows.filter((r) => r.paid).sort((a, b) => new Date(b.paidAt || 0) - new Date(a.paidAt || 0)), ...memberStatement.rows.filter((r) => !r.paid)]
      : memberStatement.rows;
    const shareWhatsApp = () => {
      const m = memberStatement.member;
      const lines = [
        `*Utheri Statement — ${m.name}*`,
        `Member: ${m.memberNumber}`,
        `Status: ${m.status}`,
        ``,
        `Total paid: KES ${memberStatement.totalPaid.toLocaleString()}`,
        `Arrears:    KES ${memberStatement.totalOwed.toLocaleString()}`,
        `Events:     ${memberStatement.rows.length}`,
        ``,
        `View / download the full PDF: ${statementsApi.memberPdfUrl(memberStatementId)}`,
      ];
      window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    };
    return (
      <div className="min-h-screen bg-background" data-testid="admin-member-statement">
        <header className="sticky top-0 z-50 bg-primary px-4 py-3 flex items-center gap-3 text-primary-foreground">
          <button onClick={() => setMemberStatementId(null)} className="text-sm underline">Back</button>
          <h1 className="font-heading text-lg font-semibold truncate flex-1">Statement: {memberStatement.member.name}</h1>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className="font-mono bg-secondary px-2 py-0.5 rounded">{memberStatement.member.memberNumber}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[memberStatement.member.status] || "bg-muted"}`}>{memberStatement.member.status}</span>
              <span>{memberStatement.member.registrationFeePaid ? "Reg. Fee Paid" : `Reg. Fee: KES ${memberStatement.member.registrationFeeAmount} (unpaid)`}</span>
            </div>
            {/* Action row: View / Download / Share */}
            <div className="flex flex-wrap gap-2 mt-3" data-testid="member-statement-actions">
              <button onClick={viewPdf} data-testid="statement-view-pdf-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-secondary text-foreground border border-border font-semibold active:scale-95">
                View PDF
              </button>
              <button onClick={downloadPdf} data-testid="statement-download-pdf-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-semibold active:scale-95">
                Download PDF
              </button>
              <button onClick={shareWhatsApp} data-testid="statement-share-whatsapp-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white font-semibold active:scale-95">
                Share via WhatsApp
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-primary font-body">KES {memberStatement.totalPaid.toLocaleString()}</p><p className="text-xs text-muted-foreground">Paid</p></div>
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-accent font-body">KES {memberStatement.totalOwed.toLocaleString()}</p><p className="text-xs text-muted-foreground">Arrears</p></div>
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold font-body">{memberStatement.rows.length}</p><p className="text-xs text-muted-foreground">Events</p></div>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border">
            <div className="px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap bg-secondary/20">
              <p className="text-xs font-medium text-muted-foreground">Contributions · PDF follows this order</p>
              <div className="inline-flex rounded-lg border border-border overflow-hidden bg-card" role="tablist" aria-label="Sort statement rows">
                {[["date", "By date of death"], ["recentPaid", "Recently paid"]].map(([key, label], i) => (
                  <button key={key} type="button" onClick={() => setStmtSort(key)} data-testid={`admin-statement-sort-${key}`} aria-pressed={stmtSort === key}
                    className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${i ? "border-l border-border" : ""} ${stmtSort === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/40"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {stmtRows.map((r) => (
              <div key={r.bereavementId} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`admin-statement-row-${r.bereavementId}`}>
                <div><p className="font-medium">{r.deceasedName}</p><p className="text-xs text-muted-foreground" data-testid={`admin-statement-date-${r.bereavementId}`}>{recent
                  ? (r.paid ? `Paid ${r.paidAt ? format(new Date(r.paidAt), "dd MMM yyyy") : "—"}` : "Not paid")
                  : (r.dateOfDeath ? format(new Date(r.dateOfDeath), "dd MMM yyyy") : "—")}</p></div>
                <div className="text-right"><span className="font-body font-semibold">KES {r.contributionAmount}</span><span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${r.paid ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>{r.paid ? "Paid" : "Unpaid"}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (selectedBereavementId && bereavementDetail) {
    const { bereavement: bd, contributions: contribs, paidCount, unpaidCount } = bereavementDetail;
    return (
      <div className="min-h-screen bg-background" data-testid="admin-bereavement-detail">
        <header className="sticky top-0 z-50 bg-primary px-4 py-3 flex items-center gap-3 text-primary-foreground"><button onClick={() => setSelectedBereavementId(null)} className="text-sm underline">Back</button><h1 className="font-heading text-lg font-semibold truncate">{bd.deceasedName}</h1></header>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <div className="bg-card border border-border rounded-xl p-5"><p className="text-sm text-muted-foreground">{bereavementSubtitle(bd)}</p><h2 className="font-heading text-2xl font-semibold">{bd.deceasedName}</h2>{bd.description && <p className="text-sm mt-2 whitespace-pre-line">{bd.description}</p>}</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-primary font-body">KES {(bd.totalContributed||0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Collected</p></div>
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold font-body">{paidCount}</p><p className="text-xs text-muted-foreground">Paid</p></div>
            <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-accent font-body">{unpaidCount}</p><p className="text-xs text-muted-foreground">Unpaid</p></div>
          </div>
          {bd.status === "active" && <button onClick={() => handleCloseBereavement(selectedBereavementId)} className="w-full py-2.5 rounded-xl bg-muted text-muted-foreground font-semibold text-sm">Close Bereavement</button>}
          <div className="flex flex-wrap gap-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/bereavement/${selectedBereavementId}/contributors.csv`}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-download-contributors-csv-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-secondary text-foreground border border-border font-semibold active:scale-95"
            >
              Download contributors (CSV)
            </a>
          </div>
          {contribs.length > 0 && (<div className="bg-card border border-border rounded-xl overflow-hidden"><div className="px-5 py-3 border-b border-border bg-secondary/30"><p className="font-heading font-semibold text-sm">Contributors ({contribs.length})</p></div><div className="divide-y divide-border">{contribs.map((c, i) => (<div key={i} className="px-5 py-3 flex items-center justify-between text-sm"><div><p className="font-medium">{c.memberName}</p><p className="text-xs text-muted-foreground">{c.memberNumber}</p></div><p className="font-semibold text-primary font-body">KES {c.amount}</p></div>))}</div></div>)}
          {arrearsMembers.length > 0 && (<div className="bg-card border border-border rounded-xl overflow-hidden"><div className="px-5 py-3 border-b border-border bg-accent/10"><p className="font-heading font-semibold text-sm text-accent">Unpaid ({arrearsMembers.length})</p></div><div className="divide-y divide-border">{arrearsMembers.map((m) => (<div key={m.id} className="px-5 py-3 flex items-center justify-between text-sm"><div><p className="font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.memberNumber}</p></div><button onClick={() => handleRecordContribution(m.id)} className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium active:scale-95 transition-transform">Record Payment</button></div>))}</div></div>)}
        </div>
      </div>
    );
  }

  // ─── Main Dashboard ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background lg:flex" data-testid="admin-dashboard">
      <Sidebar items={adminTabs} activeKey={tab} onSelect={setTab} accent="primary" title={roleLabel(user?.role) || "Admin"} />

      <div className="flex-1 lg:pl-64">
      <header className="sticky top-0 z-40 bg-primary px-4 py-3 flex items-center justify-between text-primary-foreground lg:hidden">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"><Heart className="w-3.5 h-3.5" weight="fill" /></div>
          <span className="font-heading text-lg font-bold tracking-tighter">Utheri</span>
          <span className={`text-xs px-2 py-0.5 rounded-lg ml-1 font-semibold ${roleColor(user?.role)}`} data-testid="admin-role-badge">{roleLabel(user?.role)}</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell onDark />
          <ViewToggle onDark />
          <button onClick={logout} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="admin-logout-btn"><SignOut className="w-4 h-4" /></button>
        </div>
      </header>

      {/* Desktop top bar — small breadcrumb-like header with current tab name + ViewToggle */}
      <header className="hidden lg:flex sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-8 py-3 items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-xl font-semibold capitalize">{adminTabs.find(t => t.key === tab)?.label || ""}</h1>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <ViewToggle />
        </div>
      </header>

      <div className="max-w-3xl lg:max-w-6xl mx-auto px-4 lg:px-8 py-6 space-y-5">
        {/* Member status counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-card border border-border rounded-xl p-3 text-center" data-testid="stat-active"><p className="text-xl font-bold">{activeMembers.length.toLocaleString()}</p><p className="text-xs text-muted-foreground">Active members</p></div>
          <div className="bg-card border border-border rounded-xl p-3 text-center" data-testid="stat-pending"><p className="text-xl font-bold text-yellow-600">{pendingMembers.length}</p><p className="text-xs text-muted-foreground">Pending</p></div>
          <div className="bg-card border border-border rounded-xl p-3 text-center" data-testid="stat-probation"><p className="text-xl font-bold text-purple-600">{probationMembers.length}</p><p className="text-xs text-muted-foreground">Probation</p></div>
          <div className="bg-card border border-border rounded-xl p-3 text-center" data-testid="stat-approved"><p className="text-xl font-bold text-blue-600">{approvedMembers.length}</p><p className="text-xs text-muted-foreground">Approved</p></div>
        </div>

        {/* App adoption KPI (clickable → drill-down with recent logins + never-logged-in) */}
        <LoginAdoptionStrip />

        {/* STK Push rail health — per-rail success rate over last N hours */}
        <StkRailHealthStrip />

        {/* Financial KPIs (super_admin / admin / treasurer only) — clickable */}
        {finance && ["super_admin", "admin", "treasurer"].includes(user?.role) && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2" data-testid="finance-kpi">
            <button onClick={() => setKpiTopic("income")} data-testid="kpi-income" className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold text-emerald-700">KES {Math.round(finance.totals.contributionsCollected).toLocaleString()}</p>
              <p className="text-[10px] text-emerald-900/70 mt-0.5">Total income</p>
              <p className="text-[10px] text-muted-foreground">Contrib + reg fees</p>
            </button>
            <button onClick={() => setKpiTopic("payouts")} data-testid="kpi-payouts" className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold text-orange-700">KES {Math.round(finance.totals.benefitPayouts).toLocaleString()}</p>
              <p className="text-[10px] text-orange-900/70 mt-0.5">Benefit payouts</p>
              <p className="text-[10px] text-muted-foreground">{finance.counts.payouts} payouts</p>
            </button>
            <button onClick={() => setKpiTopic("expenses")} data-testid="kpi-expenses" className="bg-red-50 border border-red-200 rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold text-red-700">KES {Math.round(finance.totals.expenses).toLocaleString()}</p>
              <p className="text-[10px] text-red-900/70 mt-0.5">Expenses</p>
              <p className="text-[10px] text-muted-foreground">{finance.counts.expenses} entries</p>
            </button>
            <button onClick={() => setKpiTopic("outstanding")} data-testid="kpi-outstanding" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold text-amber-700">KES {Math.round(finance.totals.outstanding || 0).toLocaleString()}</p>
              <p className="text-[10px] text-amber-900/70 mt-0.5">Outstanding</p>
              <p className="text-[10px] text-muted-foreground">{finance.counts.bereavementsWithShortfall || 0} events short</p>
            </button>
            <button onClick={() => setKpiTopic("net")} data-testid="kpi-net" className={`${finance.totals.netSurplus >= 0 ? "bg-primary/10 border-primary/30" : "bg-red-50 border-red-200"} border rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer`}>
              <p className={`text-base sm:text-lg font-bold ${finance.totals.netSurplus >= 0 ? "text-primary" : "text-red-700"}`}>KES {Math.round(finance.totals.netSurplus).toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Net surplus</p>
              <p className="text-[10px] text-muted-foreground">After payouts &amp; exp.</p>
            </button>
            {/* kpi-bank hidden Feb 2026 — balance does not yet reconcile with actual bank statements.
                Restore by re-adding this button and bumping grid back to lg:grid-cols-7.
            <button onClick={() => setKpiTopic("bank")} data-testid="kpi-bank" className="bg-card border border-border rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold">KES {Math.round(finance.bankAccount.balance).toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Bank balance</p>
              <p className="text-[10px] text-muted-foreground truncate">{finance.bankAccount.name || "Set in Banking"}</p>
            </button>
            */}
            <button onClick={() => setTab("wallets")} data-testid="kpi-wallet" className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-95 cursor-pointer">
              <p className="text-base sm:text-lg font-bold text-teal-700">KES {Math.round(finance.totals.walletLiabilities || 0).toLocaleString()}</p>
              <p className="text-[10px] text-teal-900/70 mt-0.5">Wallet held</p>
              <p className="text-[10px] text-muted-foreground">{finance.counts.walletsFunded || 0} funded</p>
            </button>
          </div>
        )}
        <AdminKpiDrillDown topic={kpiTopic} onClose={() => setKpiTopic(null)} />

        {/* Tabs (mobile only — desktop uses sidebar) */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl overflow-x-auto lg:hidden">
          {adminTabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex-1 min-w-max flex items-center justify-center gap-1 py-2 px-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {t.label}
              {t.count > 0 && <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${t.key === "pending" ? "bg-destructive text-destructive-foreground" : "bg-primary/15 text-primary"}`}>{t.count}</span>}
            </button>
          ))}
        </div>

        {/* ── Pending ── */}
        {tab === "pending" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between"><p className="font-heading font-semibold">Pending Registrations</p><button onClick={() => setShowAddMember(true)} data-testid="add-member-btn" className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-medium active:scale-95"><Plus className="w-3.5 h-3.5" /> Add</button></div>
            <div className="relative"><MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><input value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Search..." /></div>
            {filteredPending.length === 0 ? <div className="text-center py-12 text-muted-foreground text-sm">No pending registrations</div> : filteredPending.map((m) => (
              <div key={m.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div><p className="font-semibold">{m.name}</p><p className="text-sm text-muted-foreground">{m.phone}</p>{m.registrationDate && <p className="text-xs text-muted-foreground mt-0.5">Registered: {format(new Date(m.registrationDate), "dd MMM yyyy")}</p>}</div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => handleApprove(m.id)} data-testid={`approve-${m.id}`} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium active:scale-95"><Check className="w-3.5 h-3.5" /> Approve</button>
                    <button onClick={() => handleSuspend(m.id)} className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Membership Workflow (Approved + Probation) ── */}
        {tab === "workflow" && (
          <div className="space-y-5">
            {/* Approved - need payment */}
            {approvedMembers.length > 0 && (
              <div className="space-y-3">
                <p className="font-heading font-semibold flex items-center gap-2"><CurrencyDollar className="w-5 h-5 text-blue-600" /> Awaiting Payment ({approvedMembers.length})</p>
                <p className="text-xs text-muted-foreground">Members approved but haven't paid registration fee (KES {settings.registrationFee}) and/or cleared arrears.</p>
                {approvedMembers.map((m) => (
                  <div key={m.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{m.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">{m.ukcNumber || m.memberNumber}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS.approved}`}>Approved</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{m.registrationFeePaid ? "Reg fee paid" : `Reg fee: KES ${settings.registrationFee} (unpaid)`}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {!m.registrationFeePaid && (
                          <button onClick={() => handlePayRegistration(m.id)} className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium active:scale-95">Record Reg Fee</button>
                        )}
                        <button onClick={() => handleStartProbation(m.id)} className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white font-medium active:scale-95">Start Probation</button>
                        <button onClick={() => { setMemberStatementId(m.id); }} className="text-xs text-primary font-medium flex items-center gap-1"><Receipt className="w-3 h-3" /> Statement</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Probation */}
            {probationMembers.length > 0 && (
              <div className="space-y-3">
                <p className="font-heading font-semibold flex items-center gap-2"><ClockCountdown className="w-5 h-5 text-purple-600" /> In Probation ({probationMembers.length})</p>
                <p className="text-xs text-muted-foreground">Waiting period before full activation. Currently {settings.probationMonths} months.</p>
                {probationMembers.map((m) => {
                  const endDate = m.probationEndDate ? new Date(m.probationEndDate) : null;
                  const canActivate = endDate && endDate <= new Date();
                  return (
                    <div key={m.id} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold">{m.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">{m.ukcNumber || m.memberNumber}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS.probation}`}>Probation</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {endDate ? (canActivate ? "Probation complete — ready to activate" : `Ends: ${format(endDate, "dd MMM yyyy")}`) : "No end date set"}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          <button onClick={() => handleActivate(m.id)} className={`text-xs px-3 py-1.5 rounded-lg font-medium active:scale-95 ${canActivate ? "bg-green-600 text-white" : "bg-muted text-muted-foreground"}`}>
                            <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Activate
                          </button>
                          <button onClick={() => { setMemberStatementId(m.id); }} className="text-xs text-primary font-medium flex items-center gap-1"><Receipt className="w-3 h-3" /> Statement</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {approvedMembers.length === 0 && probationMembers.length === 0 && (
              <div className="text-center py-12 text-muted-foreground text-sm">No members in workflow currently</div>
            )}
          </div>
        )}

        {/* ── All Members ── */}
        {tab === "members" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between"><p className="font-heading font-semibold">All Members</p><button onClick={() => setShowAddMember(true)} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-medium active:scale-95"><Plus className="w-3.5 h-3.5" /> Add</button></div>
            <div className="relative"><MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} data-testid="member-search" className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Search name, phone, UKC..." /></div>
            {filteredMembers.map((m) => (
              <div key={m.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{m.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[m.status] || "bg-muted"}`}>{m.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{m.clanName} · {m.familyName} · {m.phone}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`member-last-login-${m.id}`}>
                      {m.lastLoginAt ? (
                        <>
                          <span className="text-emerald-700 font-medium">●</span> Last login: {formatDistanceToNow(new Date(m.lastLoginAt), { addSuffix: true })}
                          {m.loginCount > 1 && <span className="text-muted-foreground"> · {m.loginCount} times</span>}
                        </>
                      ) : (
                        <><span className="text-muted-foreground/60">○</span> Never logged in</>
                      )}
                    </p>
                    {m.adminRemarks && <p className="text-xs text-accent mt-0.5 italic">Note: {m.adminRemarks.slice(0, 50)}{m.adminRemarks.length > 50 ? "..." : ""}</p>}
                  </div>
                  <div className="text-right flex flex-col items-end gap-1.5 shrink-0">
                    <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">{m.ukcNumber || m.memberNumber || "—"}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setRemarksFor(m.id); setRemarksText(m.adminRemarks || ""); }}
                        className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-primary active:scale-95 transition-all"
                        title="Edit remarks"
                        aria-label="Edit remarks"
                      >
                        <NotePencil className="w-5 h-5" />
                      </button>
                      {isSuperAdmin(user) && (
                        <button
                          onClick={() => setEditMember(m)}
                          className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-primary active:scale-95 transition-all"
                          title="Edit member profile"
                          aria-label="Edit member"
                          data-testid={`edit-member-${m.id}`}
                        >
                          <Gear className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => setMemberStatementId(m.id)}
                        className="p-2 rounded-lg text-primary hover:bg-primary/10 active:scale-95 transition-all"
                        title="View member statement"
                        aria-label="View statement"
                        data-testid={`view-statement-${m.id}`}
                      >
                        <Receipt className="w-5 h-5" />
                      </button>
                      {m.status === "active" && <button onClick={() => { setExitFor(m.id); setExitType("voluntary"); }} className="text-xs text-muted-foreground hover:text-destructive" title="Exit"><UserMinus className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Bereavements ── */}
        {tab === "bereavements" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><p className="font-heading font-semibold">All Bereavements</p><button onClick={() => setShowCreateB(true)} data-testid="create-bereavement-btn" className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-medium active:scale-95"><Plus className="w-3.5 h-3.5" /> Post</button></div>
            {bereavements.map((b) => (
              <div key={b.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                <div className="flex gap-3 items-start">
                  {b.imageUrl ? (
                    <img src={b.imageUrl} alt={b.deceasedName} className="w-14 h-14 rounded-lg object-cover bg-muted border border-border shrink-0" data-testid={`admin-bereavement-thumb-${b.id}`} />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-muted border border-border shrink-0 flex items-center justify-center"><CalendarDots className="w-5 h-5 text-muted-foreground opacity-40" /></div>
                  )}
                  <button className="flex-1 text-left min-w-0" onClick={() => setSelectedBereavementId(b.id)}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{b.relationship}</span>{b.status === "active" ? <span className="text-xs text-green-700 font-medium">Active</span> : <span className="text-xs text-muted-foreground">Closed</span>}</div>
                    <p className="font-heading font-semibold truncate">{b.deceasedName}</p>
                    <p className="text-xs text-muted-foreground truncate">{bereavementSubtitle(b)}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground"><span className="flex items-center gap-1"><CalendarDots className="w-3 h-3" />{b.dateOfDeath ? format(new Date(b.dateOfDeath), "dd MMM yyyy") : "—"}</span><span className="font-semibold text-primary">KES {(b.totalContributed||0).toLocaleString()}</span></div>
                  </button>
                  <button onClick={() => openEditBereavement(b)} data-testid={`edit-bereavement-${b.id}`} className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary transition-colors" title="Edit obituary">
                    <NotePencil className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Areas ── */}
        {tab === "areas" && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-5"><p className="font-heading font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Add Area</p><div className="flex gap-2"><input value={newArea} onChange={(e) => setNewArea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddArea()} placeholder="e.g. Westlands..." className="flex-1 px-3 py-2 text-sm bg-card border border-input rounded-xl" /><button onClick={handleAddArea} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95"><Plus className="w-4 h-4" /></button></div></div>
            {areas.map((a) => (<div key={a.id} className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{a.name}</span></div><button onClick={() => handleDeleteArea(a.id)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg"><Trash className="w-4 h-4" /></button></div>))}
          </div>
        )}

        {/* ── Announcements ── */}
        {tab === "announcements" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><p className="font-heading font-semibold">Announcements</p><button onClick={() => setShowCreateA(true)} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-medium active:scale-95"><Plus className="w-3.5 h-3.5" /> New</button></div>
            {announcements.map((a) => (<div key={a.id} className="bg-card border border-border rounded-xl p-5"><div className="flex items-center justify-between mb-2"><h3 className="font-heading font-semibold">{a.title}</h3><button onClick={() => handleDeleteAnnouncement(a.id)} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash className="w-3.5 h-3.5" /></button></div><p className="text-sm whitespace-pre-line">{a.body}</p><p className="text-xs text-muted-foreground mt-2">{a.createdAt ? format(new Date(a.createdAt), "dd MMM yyyy") : ""}</p></div>))}
          </div>
        )}

        {/* ── Banking & Expenses ── */}
        {tab === "banking" && <BankingTab />}
        {tab === "wallets" && <WalletsAdminTab />}
        {tab === "payments" && <PaymentsTab onRefresh={fetchData} />}
        {tab === "reminders" && <RemindersTab />}

        {/* ── Reports ── */}
        {tab === "reports" && treasury && (
          <div className="space-y-4" data-testid="admin-reports">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><ChartBar className="w-5 h-5" /> Treasury</h2>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleCsvExport("members")}
                  disabled={csvBusy === "members"}
                  data-testid="export-members-csv-btn"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium hover:bg-secondary/70 active:scale-95 disabled:opacity-60 transition-all"
                >
                  <DownloadSimple className="w-4 h-4" weight="bold" />
                  {csvBusy === "members" ? "Exporting…" : "Members CSV"}
                </button>
                <button
                  type="button"
                  onClick={() => handleCsvExport("contributions")}
                  disabled={csvBusy === "contributions"}
                  data-testid="export-contributions-csv-btn"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 active:scale-95 disabled:opacity-60 transition-all"
                >
                  <DownloadSimple className="w-4 h-4" weight="bold" />
                  {csvBusy === "contributions" ? "Exporting…" : "Contributions CSV"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-primary font-body">KES {treasury.totalCollected.toLocaleString()}</p><p className="text-xs text-muted-foreground">Collected</p></div>
              <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold text-accent font-body">KES {treasury.outstanding.toLocaleString()}</p><p className="text-xs text-muted-foreground">Outstanding</p></div>
              <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold font-body">{treasury.collectionRate}%</p><p className="text-xs text-muted-foreground">Collection Rate</p></div>
              <div className="bg-card border border-border rounded-xl p-4 text-center"><p className="text-xl font-bold font-body">{treasury.membersInArrears}</p><p className="text-xs text-muted-foreground">In Arrears</p></div>
            </div>
            {treasury.topContributors?.length > 0 && (<div className="bg-card border border-border rounded-xl overflow-hidden"><div className="px-5 py-3 border-b border-border bg-secondary/30"><p className="font-heading font-semibold text-sm">Top Contributors</p></div><div className="divide-y divide-border">{treasury.topContributors.map((c, i) => (<div key={i} className="px-5 py-3 flex items-center justify-between text-sm"><div><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.memberNumber}</p></div><p className="font-semibold text-primary font-body">KES {c.total.toLocaleString()}</p></div>))}</div></div>)}
          </div>
        )}

        {/* ── Team & Roles ── */}
        {tab === "team" && (
          <div className="space-y-4" data-testid="admin-team">
            <div>
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><UserCircle className="w-5 h-5" /> Team & Roles</h2>
              <p className="text-sm text-muted-foreground mt-1">Assign welfare-group roles. Treasurers handle contributions & reports. Secretaries handle member workflow, obituaries & notices.{user?.role !== "super_admin" && " The Super Admin role can only be assigned by another Super Admin."}</p>
            </div>

            {/* Current team */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="team-current">
              <div className="px-5 py-3 border-b border-border bg-secondary/30"><p className="font-heading font-semibold text-sm">Current team ({teamMembers.length})</p></div>
              <div className="divide-y divide-border">
                {teamMembers.length === 0 ? <p className="px-5 py-6 text-center text-sm text-muted-foreground">No team members yet.</p> :
                  teamMembers.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 px-5 py-3" data-testid={`team-row-${a.id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{a.name}</p>
                        <p className="text-xs text-muted-foreground">{a.phone} · {a.ukcNumber || a.memberNumber || "—"}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.role === "super_admin" ? "bg-amber-100 text-amber-900" :
                          a.role === "admin" ? "bg-blue-100 text-blue-800" :
                          a.role === "treasurer" ? "bg-emerald-100 text-emerald-800" :
                          a.role === "secretary" ? "bg-indigo-100 text-indigo-800" :
                          "bg-slate-100 text-slate-700"
                        }`}>{roleLabel(a.role)}</span>
                        {(() => {
                          const callerIsSuper = isSuperAdmin(user);
                          const targetIsSuper = a.role === "super_admin";
                          const canEdit = callerIsSuper || !targetIsSuper;
                          if (!canEdit) return <span className="text-xs text-muted-foreground">Super Admin only</span>;
                          return (
                            <select value={a.role} onChange={(e) => handleSetTeamRole(a.id, e.target.value)} className="px-2 py-1 text-xs bg-card border border-input rounded-lg" data-testid={`team-role-${a.id}`}>
                              {callerIsSuper && <option value="super_admin">Super Admin</option>}
                              <option value="admin">Welfare Admin</option>
                              <option value="treasurer">Treasurer</option>
                              <option value="secretary">Secretary</option>
                              <option value="member">Demote to Member</option>
                            </select>
                          );
                        })()}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* Add new team member (search any member) */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="team-add">
              <p className="font-heading font-semibold text-sm">Promote a member</p>
              <p className="text-xs text-muted-foreground">Search by name / phone / UKC number (min 2 chars), then pick a role.</p>
              <input
                value={memberSearchQ}
                onChange={(e) => handleSearchMembers(e.target.value)}
                placeholder="Search active members..."
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
                data-testid="team-search-input"
              />
              {memberSearchResults.length > 0 && (
                <div className="border border-border rounded-xl divide-y divide-border max-h-80 overflow-y-auto" data-testid="team-search-results">
                  {memberSearchResults.map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.phone} · {m.ukcNumber} · {roleLabel(m.role || "member")}</p>
                      </div>
                      <select
                        defaultValue=""
                        onChange={(e) => { if (e.target.value) handleSetTeamRole(m.id, e.target.value); }}
                        className="px-2 py-1 text-xs bg-card border border-input rounded-lg"
                        data-testid={`team-assign-role-${m.id}`}
                      >
                        <option value="" disabled>Assign role</option>
                        {isSuperAdmin(user) && <option value="super_admin">Super Admin</option>}
                        <option value="admin">Welfare Admin</option>
                        <option value="treasurer">Treasurer</option>
                        <option value="secretary">Secretary</option>
                        <option value="member">Member</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}
              {memberSearchQ && memberSearchQ.length >= 2 && memberSearchResults.length === 0 && (
                <p className="text-xs text-muted-foreground">No matches found.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Settings ── */}
        {tab === "settings" && (
          <div className="space-y-4" data-testid="admin-settings">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Gear className="w-5 h-5" /> Settings</h2>
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              {editSettings ? (
                <>
                  <div><label className="text-sm font-medium block mb-1">Registration Fee (KES)</label><input type="number" value={sForm.registrationFee ?? settings.registrationFee} onChange={(e) => setSForm(f => ({...f, registrationFee: Number(e.target.value)}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>
                  <div><label className="text-sm font-medium block mb-1">Probation Period (months)</label><input type="number" min="1" max="12" value={sForm.probationMonths ?? settings.probationMonths} onChange={(e) => setSForm(f => ({...f, probationMonths: Number(e.target.value)}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>
                  <div><label className="text-sm font-medium block mb-1">Default Contribution Amount (KES)</label><input type="number" value={sForm.contributionAmount ?? settings.contributionAmount} onChange={(e) => setSForm(f => ({...f, contributionAmount: Number(e.target.value)}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                    <div><label className="text-sm font-medium block mb-1">Wallet min top-up (KES)</label><input type="number" data-testid="setting-wallet-min" value={sForm.walletMinTopup ?? (settings.walletMinTopup || 200)} onChange={(e) => setSForm(f => ({...f, walletMinTopup: Number(e.target.value)}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>
                    <div><label className="text-sm font-medium block mb-1">Wallet max top-up (KES)</label><input type="number" data-testid="setting-wallet-max" value={sForm.walletMaxTopup ?? (settings.walletMaxTopup || 50000)} onChange={(e) => setSForm(f => ({...f, walletMaxTopup: Number(e.target.value)}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>
                  </div>
                  <div className="flex gap-2"><button onClick={handleSaveSettings} data-testid="save-settings-btn" className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95">Save</button><button onClick={() => setEditSettings(false)} className="px-4 py-2 rounded-xl bg-muted text-muted-foreground font-medium text-sm">Cancel</button></div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-secondary/30 rounded-xl p-4"><p className="text-xs text-muted-foreground">Registration Fee</p><p className="text-2xl font-bold font-body">KES {settings.registrationFee}</p></div>
                    <div className="bg-secondary/30 rounded-xl p-4"><p className="text-xs text-muted-foreground">Probation Period</p><p className="text-2xl font-bold font-body">{settings.probationMonths} months</p></div>
                    <div className="bg-secondary/30 rounded-xl p-4"><p className="text-xs text-muted-foreground">Contribution Amount</p><p className="text-2xl font-bold font-body">KES {settings.contributionAmount}</p></div>
                    <div className="bg-secondary/30 rounded-xl p-4"><p className="text-xs text-muted-foreground">Wallet min / max</p><p className="text-base font-bold font-body">KES {settings.walletMinTopup || 200} – {settings.walletMaxTopup || 50000}</p></div>
                  </div>
                  <button onClick={() => { setEditSettings(true); setSForm(settings); }} data-testid="edit-settings-btn" className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95"><Gear className="w-4 h-4 inline mr-1" /> Edit Settings</button>
                </>
              )}
            </div>

            {/* ── Mass Import Register (Super Admin only — also accessible from /system) ────────────── */}
            {user?.role === "super_admin" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="import-register-section">
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><ArrowRight className="w-5 h-5 text-primary" /> Mass Import Register</h3>
                <p className="text-xs text-muted-foreground mt-1">Import existing Utheri register (members + historical bereavement contributions) from the bundled <code className="bg-muted px-1 py-0.5 rounded">utheri_register.json</code>.</p>
              </div>

              {!importPreview ? (
                <button onClick={handleLoadPreview} data-testid="load-register-preview-btn" className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm active:scale-95">Load Preview</button>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div className="bg-secondary/30 rounded-xl p-3"><p className="text-xl font-bold font-body">{importPreview.totalMembers}</p><p className="text-xs text-muted-foreground">Total Members</p></div>
                    <div className="bg-secondary/30 rounded-xl p-3"><p className="text-xl font-bold font-body">{importPreview.withPhone}</p><p className="text-xs text-muted-foreground">With Phone</p></div>
                    <div className="bg-secondary/30 rounded-xl p-3"><p className="text-xl font-bold font-body">{importPreview.fullyPaidAllBereavements}</p><p className="text-xs text-muted-foreground">Fully Paid</p></div>
                    <div className="bg-secondary/30 rounded-xl p-3"><p className="text-xl font-bold font-body">UKC-{String(importPreview.maxUkcNumber).padStart(4, "0")}</p><p className="text-xs text-muted-foreground">Highest UKC</p></div>
                  </div>
                  <div className="bg-secondary/10 rounded-xl p-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Historical Bereavements (will be created as closed)</p>
                    <div className="flex flex-wrap gap-2">
                      {importPreview.bereavements.map((b) => (
                        <span key={b.index} className="text-xs px-2 py-1 bg-card border border-border rounded-lg">
                          {b.order}. {b.title} — KES {b.amount_per_member}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => handleRunImport(false)} disabled={importing} data-testid="import-append-btn" className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95 disabled:opacity-60">{importing ? "Importing..." : "Import (append)"}</button>
                    <button onClick={() => setConfirmReset(true)} disabled={importing} data-testid="import-reset-btn" className="px-4 py-2 rounded-xl bg-destructive text-destructive-foreground font-medium text-sm active:scale-95 disabled:opacity-60">Reset & Import</button>
                    <button onClick={() => { setImportPreview(null); setImportResult(null); }} disabled={importing} className="px-4 py-2 rounded-xl bg-muted text-muted-foreground font-medium text-sm">Close</button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold">Append</span> adds new members (skips duplicates by phone+UKC). <span className="font-semibold text-destructive">Reset</span> wipes all non-admin users, bereavements & contributions first.
                  </p>
                </>
              )}

              {importResult && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1 text-sm" data-testid="import-result">
                  <p className="font-semibold text-green-800">Import complete</p>
                  <p>Imported: <span className="font-mono">{importResult.imported}</span> · Skipped: <span className="font-mono">{importResult.skipped}</span></p>
                  <p>Phone collisions (relatives): <span className="font-mono">{importResult.phoneCollisions}</span> · UKC collisions: <span className="font-mono">{importResult.ukcCollisions}</span></p>
                  <p>Bereavements: <span className="font-mono">{importResult.bereavementsCreated}</span> · Contributions: <span className="font-mono">{importResult.contributionsRecorded}</span></p>
                  {importResult.totalContributedByBereavement && (
                    <div className="pt-1 border-t border-green-200 mt-1">
                      {Object.entries(importResult.totalContributedByBereavement).map(([k, v]) => (
                        <p key={k} className="text-xs">{k}: KES {v.toLocaleString()}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* ── Email Notifications (Super Admin only — also accessible from /system) ────────────── */}
            {user?.role === "super_admin" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="email-notifications-section">
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Email Notifications (quick test)</h3>
                <p className="text-xs text-muted-foreground mt-1">Send a test email using the currently saved SMTP config. To change credentials, open <Link to="/system" className="text-primary underline">System → Email</Link>.</p>
              </div>
              {emailStatus ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${emailStatus.enabled ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`} data-testid="email-status-badge">
                      {emailStatus.enabled ? "Enabled" : "Disabled"}
                    </span>
                    {!emailStatus.enabled && <span className="text-xs text-muted-foreground">SMTP not configured</span>}
                  </div>
                  {emailStatus.enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div><p className="text-xs text-muted-foreground">SMTP Host</p><p className="font-mono">{emailStatus.host}:{emailStatus.port}</p></div>
                      <div><p className="text-xs text-muted-foreground">From</p><p className="font-mono">{emailStatus.fromName} &lt;{emailStatus.fromEmail}&gt;</p></div>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <input type="email" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="test recipient email" className="flex-1 px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="test-email-input" />
                    <button onClick={handleSendTestEmail} disabled={sendingTest || !emailStatus.enabled} data-testid="send-test-email-btn" className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm active:scale-95 disabled:opacity-60">{sendingTest ? "Sending..." : "Send test"}</button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
            </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────────── */}
      {showCreateB && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="create-bereavement-modal"><div className="flex items-center justify-between"><h3 className="font-heading text-xl font-semibold">Post Obituary</h3><button onClick={() => setShowCreateB(false)}><X className="w-5 h-5" /></button></div>
        <div>
          <label className="text-sm font-medium block mb-1">Photo (optional)</label>
          <div className="flex items-center gap-3">
            {bForm.imageUrl ? (
              <img src={bForm.imageUrl} alt="preview" className="w-16 h-16 rounded-xl object-cover border border-border" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-muted border border-border flex items-center justify-center text-xs text-muted-foreground">No photo</div>
            )}
            <div className="flex-1 flex flex-wrap gap-2">
              <label className="px-3 py-2 text-xs rounded-xl bg-secondary text-secondary-foreground font-medium cursor-pointer">
                {bImageProcessing ? "Processing..." : (bForm.imageUrl ? "Change photo" : "Upload photo")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBImageChange(e.target.files?.[0])} data-testid="create-bereavement-image-input" />
              </label>
              {bForm.imageUrl && <button type="button" onClick={() => setBForm(f => ({ ...f, imageUrl: "" }))} className="px-3 py-2 text-xs rounded-xl bg-muted text-muted-foreground font-medium">Remove</button>}
            </div>
          </div>
        </div>
        <div><label className="text-sm font-medium block mb-1">Deceased Name *</label><input value={bForm.deceasedName} onChange={(e) => setBForm(f => ({...f, deceasedName: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div><div><label className="text-sm font-medium block mb-1">Relationship *</label><select value={bForm.relationship} onChange={(e) => setBForm(f => ({...f, relationship: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"><option value="">Select</option>{RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}</select></div><div><label className="text-sm font-medium block mb-1">Member *</label><select value={bForm.memberId} onChange={(e) => setBForm(f => ({...f, memberId: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"><option value="">Select</option>{activeMembers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.ukcNumber || m.memberNumber})</option>)}</select></div><div><label className="text-sm font-medium block mb-1">Date of Death *</label><input type="date" value={bForm.dateOfDeath} onChange={(e) => setBForm(f => ({...f, dateOfDeath: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div><div><label className="text-sm font-medium block mb-1">Amount (KES, default: {settings.contributionAmount})</label><input type="number" value={bForm.contributionAmount} onChange={(e) => setBForm(f => ({...f, contributionAmount: e.target.value}))} placeholder={String(settings.contributionAmount)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div><div><label className="text-sm font-medium block mb-1">Description</label><textarea rows={3} value={bForm.description} onChange={(e) => setBForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" /></div><button onClick={handleCreateBereavement} disabled={bSubmitting || bImageProcessing} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60">{bSubmitting ? "Posting..." : "Post Obituary"}</button></div></div>)}

      {/* ── Edit Obituary Modal ─────────────────────────────────────────────── */}
      {editBereavement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="edit-bereavement-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-semibold">Edit Obituary</h3>
              <button onClick={() => setEditBereavement(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Posted by {editBereavement.memberName} · {editBereavement.status === "active" ? "Active" : "Closed"}</p>

            <div>
              <label className="text-sm font-medium block mb-1">Photo</label>
              <div className="flex items-center gap-3">
                {eForm.imageUrl ? (
                  <img src={eForm.imageUrl} alt="preview" className="w-16 h-16 rounded-xl object-cover border border-border" data-testid="edit-bereavement-image-preview" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-muted border border-border flex items-center justify-center text-xs text-muted-foreground">No photo</div>
                )}
                <div className="flex-1 flex flex-wrap gap-2">
                  <label className="px-3 py-2 text-xs rounded-xl bg-secondary text-secondary-foreground font-medium cursor-pointer">
                    {eImageProcessing ? "Processing..." : (eForm.imageUrl ? "Change photo" : "Upload photo")}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleEImageChange(e.target.files?.[0])} data-testid="edit-bereavement-image-input" />
                  </label>
                  {eForm.imageUrl && <button type="button" onClick={() => setEForm(f => ({ ...f, imageUrl: "" }))} className="px-3 py-2 text-xs rounded-xl bg-muted text-muted-foreground font-medium" data-testid="edit-bereavement-image-remove">Remove</button>}
                </div>
              </div>
            </div>

            <div><label className="text-sm font-medium block mb-1">Deceased Name *</label><input value={eForm.deceasedName} onChange={(e) => setEForm(f => ({...f, deceasedName: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="edit-bereavement-name" /></div>
            <div><label className="text-sm font-medium block mb-1">Relationship *</label><select value={eForm.relationship} onChange={(e) => setEForm(f => ({...f, relationship: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="edit-bereavement-relationship">{RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
            <div><label className="text-sm font-medium block mb-1">Date of Death *</label><input type="date" value={eForm.dateOfDeath} onChange={(e) => setEForm(f => ({...f, dateOfDeath: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="edit-bereavement-dateOfDeath" /></div>
            <div><label className="text-sm font-medium block mb-1">Amount (KES)</label><input type="number" value={eForm.contributionAmount} onChange={(e) => setEForm(f => ({...f, contributionAmount: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="edit-bereavement-amount" /></div>
            <div><label className="text-sm font-medium block mb-1">Description</label><textarea rows={4} value={eForm.description} onChange={(e) => setEForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" data-testid="edit-bereavement-description" /></div>
            <button onClick={handleEditBereavement} disabled={eSubmitting || eImageProcessing} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60" data-testid="edit-bereavement-save-btn">{eSubmitting ? "Saving..." : "Save Changes"}</button>
          </div>
        </div>
      )}

      {showCreateA && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4"><div className="flex items-center justify-between"><h3 className="font-heading text-xl font-semibold">New Announcement</h3><button onClick={() => setShowCreateA(false)}><X className="w-5 h-5" /></button></div><div><label className="text-sm font-medium block mb-1">Title</label><input value={aForm.title} onChange={(e) => setAForm(f => ({...f, title: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div><div><label className="text-sm font-medium block mb-1">Body</label><textarea rows={4} value={aForm.body} onChange={(e) => setAForm(f => ({...f, body: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" /></div><div><label className="text-sm font-medium block mb-1">Priority</label><select value={aForm.priority} onChange={(e) => setAForm(f => ({...f, priority: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"><option value="normal">Normal</option><option value="urgent">Urgent</option></select></div><button onClick={handleCreateAnnouncement} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm">Post</button></div></div>)}

      {showAddMember && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="add-member-modal"><div className="flex items-center justify-between"><h3 className="font-heading text-xl font-semibold">Add Member</h3><button onClick={() => setShowAddMember(false)}><X className="w-5 h-5" /></button></div>{[{l:"Name *",k:"name"},{l:"Phone *",k:"phone",t:"tel"},{l:"Clan",k:"clanName"},{l:"Family",k:"familyName"},{l:"Next of Kin",k:"nextOfKinName"},{l:"Next of Kin Phone",k:"nextOfKinPhone",t:"tel"}].map(({l,k,t}) => (<div key={k}><label className="text-sm font-medium block mb-1">{l}</label><input type={t||"text"} value={mForm[k]} onChange={(e) => setMForm(f => ({...f, [k]: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div>))}<div><label className="text-sm font-medium block mb-1">Registration Date</label><input type="date" value={mForm.registrationDate} onChange={(e) => setMForm(f => ({...f, registrationDate: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" /></div><div><label className="text-sm font-medium block mb-1">Status</label><select value={mForm.status} onChange={(e) => setMForm(f => ({...f, status: e.target.value}))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"><option value="active">Active (imported)</option><option value="approved">Approved (needs payment)</option><option value="pending">Pending</option></select></div><button onClick={handleAddMember} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm">Add Member</button></div></div>)}

      {/* Remarks Modal */}
      {remarksFor && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4"><div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Admin Remarks</h3><button onClick={() => setRemarksFor(null)}><X className="w-5 h-5" /></button></div><textarea rows={4} value={remarksText} onChange={(e) => setRemarksText(e.target.value)} placeholder="Add notes about this member..." className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" data-testid="remarks-textarea" /><button onClick={handleSaveRemarks} data-testid="save-remarks-btn" className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm">Save Remarks</button></div></div>)}

      {editMember && <EditMemberDialog member={editMember} areas={areas} onClose={() => setEditMember(null)} onSaved={() => { setEditMember(null); fetchData(); }} />}

      {/* Exit Modal */}
      {exitFor && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4"><div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Member Exit</h3><button onClick={() => setExitFor(null)}><X className="w-5 h-5" /></button></div><p className="text-sm text-muted-foreground">UKC number will be preserved but deactivated.</p><div><label className="text-sm font-medium block mb-1">Exit Type</label><select value={exitType} onChange={(e) => setExitType(e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"><option value="voluntary">Voluntary Exit</option><option value="deceased">Deceased</option></select></div><div><label className="text-sm font-medium block mb-1">Remarks</label><textarea rows={2} value={exitRemarks} onChange={(e) => setExitRemarks(e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" /></div><button onClick={handleExit} data-testid="confirm-exit-btn" className="w-full py-2.5 rounded-xl bg-destructive text-destructive-foreground font-semibold text-sm">Confirm Exit</button></div></div>)}

      {/* Confirm Reset-Import Modal */}
      {confirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4" data-testid="confirm-reset-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-destructive">Reset & Import — Are you sure?</h3>
              <button onClick={() => setConfirmReset(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 space-y-1 text-sm">
              <p className="font-semibold text-destructive">This will delete:</p>
              <ul className="list-disc list-inside text-xs">
                <li>All non-admin members</li>
                <li>All bereavements (obituaries)</li>
                <li>All contributions</li>
              </ul>
              <p className="text-xs pt-1">Admin accounts are preserved. The register file will then be imported fresh.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleRunImport(true)} disabled={importing} data-testid="confirm-reset-import-btn" className="flex-1 py-2.5 rounded-xl bg-destructive text-destructive-foreground font-semibold text-sm disabled:opacity-60">{importing ? "Importing..." : "Yes, Reset & Import"}</button>
              <button onClick={() => setConfirmReset(false)} disabled={importing} className="flex-1 py-2.5 rounded-xl bg-muted text-muted-foreground font-semibold text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit Member Dialog (super admin only) ────────────────────────────────────
function EditMemberDialog({ member, areas, onClose, onSaved }) {
  // Uncontrolled form: a single ref holds every field's current value. Each
  // <input> writes to formRef.current on its own onChange — no React state
  // updates, so the parent never re-renders mid-typing, so inputs never lose
  // focus. This is the cleanest possible cure for the "one character per
  // click" focus-loss bug.
  const formRef = useRef({
    name: member.name || "",
    phone: member.phone || "",
    email: member.email || "",
    clanName: member.clanName || "",
    familyName: member.familyName || "",
    areaId: member.areaId || "",
    spouseName: member.spouseName || "",
    spouseAge: member.spouseAge || "",
    nextOfKinName: member.nextOfKinName || "",
    nextOfKinPhone: member.nextOfKinPhone || "",
    occupation: member.occupation || "",
    idNumber: member.idNumber || "",
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = formRef.current;
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.phone.trim()) { toast.error("Phone is required"); return; }
    setSaving(true);
    try {
      await usersApi.editMember(member.id, f);
      toast.success("Member updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update member");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    const phone = formRef.current.phone?.trim() || member.phone;
    const digits = (phone || "").replace(/\D/g, "");
    const preview = digits.slice(-4);
    if (digits.length < 4) {
      toast.error("Add a valid phone number first, then reset.");
      return;
    }
    if (!window.confirm(
      `Reset password to last 4 digits of the phone (${preview})?\n\n` +
      `This overrides any password the member may have set. They will need to log in with their phone + ${preview}.`
    )) return;
    setResetting(true);
    try {
      const { data } = await adminToolsApi.resetPasswordToDefault(member.id);
      toast.success(`Password reset to ${data.newPassword}`, { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setResetting(false);
    }
  };

  const fld = (label, k, type = "text", placeholder = "") => (
    <div key={k}>
      <label className="text-xs font-medium text-foreground block mb-1">{label}</label>
      <input
        type={type}
        defaultValue={formRef.current[k] || ""}
        onChange={(e) => { formRef.current[k] = e.target.value; }}
        placeholder={placeholder}
        data-testid={`edit-member-${k}`}
        className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="edit-member-dialog">
        <div className="flex items-center justify-between sticky top-0 bg-card z-10 pb-2 border-b border-border">
          <div>
            <h3 className="font-heading text-lg font-semibold">Edit Member</h3>
            <p className="text-xs text-muted-foreground">{member.ukcNumber || member.memberNumber || "—"} · {member.status}</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fld("Full name *", "name", "text", "Member full name")}
            {fld("Phone *", "phone", "tel", "0728607774")}
            {fld("Email", "email", "email", "member@example.com")}
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Area</label>
              <select
                defaultValue={formRef.current.areaId || ""}
                onChange={(e) => { formRef.current.areaId = e.target.value; }}
                data-testid="edit-member-areaId"
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— None —</option>
                {(areas || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            {fld("Clan", "clanName")}
            {fld("Family", "familyName")}
            {fld("ID number", "idNumber")}
            {fld("Occupation", "occupation")}
            {fld("Spouse name", "spouseName")}
            {fld("Spouse age", "spouseAge", "number")}
            {fld("Next of kin name", "nextOfKinName")}
            {fld("Next of kin phone", "nextOfKinPhone", "tel")}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="reset-password-row">
            <div className="text-xs text-amber-900 flex-1">
              <p className="font-semibold mb-0.5">Reset login password</p>
              <p className="text-amber-800/80">Sets password to the <strong>last 4 digits of the current phone</strong>. Use this after adding a phone for a member who can&apos;t log in.</p>
            </div>
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={resetting}
              data-testid="reset-password-btn"
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold whitespace-nowrap active:scale-95 disabled:opacity-50"
            >
              {resetting ? "Resetting…" : "Reset password"}
            </button>
          </div>
          <div className="flex gap-2 pt-3 border-t border-border sticky bottom-0 bg-card">
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-member-save-btn"
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-secondary text-secondary-foreground border border-border font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Login Adoption KPI strip + drill-down modal ──────────────────────────
function LoginAdoptionStrip() {
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("recent"); // recent | never

  useEffect(() => {
    adminToolsApi.loginActivity()
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  if (!stats) return null;
  const pct = stats.adoptionPct || 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="kpi-login-adoption"
        className="w-full flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5 hover:shadow-md transition-all active:scale-[0.99] cursor-pointer text-left"
      >
        <div>
          <p className="text-[10px] text-indigo-900/70 uppercase tracking-wide">App adoption</p>
          <p className="text-base font-bold text-indigo-700"><span data-testid="login-adoption-count">{stats.everLoggedIn} / {stats.totalMembers}</span> members logged in</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-indigo-700">{pct}%</p>
          <p className="text-[10px] text-muted-foreground">Tap for details</p>
        </div>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="login-adoption-modal">
          <div className="bg-card w-full sm:max-w-2xl sm:rounded-xl rounded-t-2xl border border-border max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold">App adoption</h3>
                <p className="text-xs text-muted-foreground">{stats.everLoggedIn} of {stats.totalMembers} members have logged in ({pct}%)</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-emerald-700">{stats.active7d}</p>
                <p className="text-[10px] text-muted-foreground">Last 7 days</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-blue-700">{stats.active30d}</p>
                <p className="text-[10px] text-muted-foreground">Last 30 days</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-purple-700">{stats.active90d}</p>
                <p className="text-[10px] text-muted-foreground">Last 90 days</p>
              </div>
            </div>

            <div className="flex gap-1 border-b border-border">
              <button
                onClick={() => setActiveTab("recent")}
                data-testid="adoption-tab-recent"
                className={`px-3 py-2 text-sm font-medium ${activeTab === "recent" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
              >Recent logins ({stats.recentLogins.length})</button>
              <button
                onClick={() => setActiveTab("never")}
                data-testid="adoption-tab-never"
                className={`px-3 py-2 text-sm font-medium ${activeTab === "never" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
              >Never logged in ({stats.neverLoggedIn})</button>
            </div>

            {activeTab === "recent" ? (
              <div className="bg-card border border-border rounded-xl divide-y divide-border max-h-96 overflow-y-auto">
                {stats.recentLogins.length === 0
                  ? <p className="px-4 py-6 text-sm text-muted-foreground text-center">No logins recorded yet.</p>
                  : stats.recentLogins.map(r => (
                    <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{r.name}</p>
                        <p className="text-[11px] text-muted-foreground">{r.memberNumber} · {r.phone} · {r.status}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[11px] text-emerald-700 font-medium whitespace-nowrap">{r.lastLoginAt ? formatDistanceToNow(new Date(r.lastLoginAt), { addSuffix: true }) : "—"}</p>
                        <p className="text-[10px] text-muted-foreground">{r.loginCount} login{r.loginCount !== 1 ? "s" : ""}</p>
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Showing up to 50 — the full {stats.neverLoggedIn} list can be reached from <strong>Reminders → Send SMS</strong>.</p>
                <div className="bg-card border border-border rounded-xl divide-y divide-border max-h-96 overflow-y-auto">
                  {stats.neverLoggedInSample.length === 0
                    ? <p className="px-4 py-6 text-sm text-muted-foreground text-center">Everyone has logged in!</p>
                    : stats.neverLoggedInSample.map(r => (
                      <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{r.name}</p>
                          <p className="text-[11px] text-muted-foreground">{r.memberNumber} · {r.phone} · {r.status}</p>
                        </div>
                        <span className="text-[10px] text-orange-700 font-medium whitespace-nowrap">Never</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


// ─── STK Rail Health widget ──────────────────────────────────────────────
// Surfaces per-rail (M-Pesa vs LKS) STK success rate over the last 24h.
// Designed for spotting a misconfigured tenant rail in seconds. Clicking
// either rail card opens a drill-down modal with the last 50 attempts.
function StkRailHealthStrip() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [windowHours, setWindowHours] = useState(24);

  useEffect(() => {
    adminToolsApi.stkHealth(windowHours, 50)
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, [windowHours]);

  if (!data) return null;
  const total = data.rails.reduce((s, r) => s + r.total, 0);
  if (total === 0) return null; // hide when no traffic yet

  const railColor = (r) => {
    if (r.total === 0) return "border-border bg-card text-muted-foreground";
    if (r.successPct === null) return "border-amber-300 bg-amber-50 text-amber-800";
    if (r.successPct >= 90) return "border-emerald-300 bg-emerald-50 text-emerald-800";
    if (r.successPct >= 60) return "border-amber-300 bg-amber-50 text-amber-800";
    return "border-red-300 bg-red-50 text-red-800";
  };

  return (
    <>
      <div className="space-y-1" data-testid="stk-rail-health-strip">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-muted-foreground">STK push health · last {windowHours}h</p>
          <button onClick={() => setOpen(true)} data-testid="stk-rail-health-details-btn" className="text-[11px] text-primary font-semibold">Details →</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {data.rails.map(r => (
            <button
              key={r.rail}
              onClick={() => setOpen(true)}
              data-testid={`stk-rail-card-${r.rail}`}
              className={`rounded-xl p-3 text-left border-2 transition-all hover:shadow-md active:scale-[0.99] ${railColor(r)}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{r.rail}{data.activeRail === r.rail && <span className="ml-1 text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded">ACTIVE</span>}</p>
                <p className="text-base font-bold">{r.successPct == null ? "—" : `${r.successPct}%`}</p>
              </div>
              <p className="text-[11px] opacity-80 mt-0.5">
                {r.success} ok &middot; {r.failed} failed{r.pending > 0 && ` · ${r.pending} pending`}
              </p>
              <p className="text-[10px] opacity-60">{r.total} attempt{r.total !== 1 ? "s" : ""}</p>
            </button>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="stk-rail-health-modal">
          <div className="bg-card w-full sm:max-w-2xl sm:rounded-xl rounded-t-2xl border border-border max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold">STK push health</h3>
                <p className="text-xs text-muted-foreground">Last {data.recentAttempts.length} attempts &middot; active rail: <span className="uppercase font-semibold">{data.activeRail}</span></p>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex gap-1 border-b border-border">
              {[24, 72, 168].map(h => (
                <button
                  key={h}
                  onClick={() => setWindowHours(h)}
                  data-testid={`stk-health-window-${h}`}
                  className={`px-3 py-2 text-xs font-medium ${windowHours === h ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
                >Last {h === 24 ? "24h" : h === 72 ? "3 days" : "7 days"}</button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {data.rails.map(r => (
                <div key={r.rail} className={`rounded-xl p-3 border ${railColor(r)}`}>
                  <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{r.rail}</p>
                  <p className="text-2xl font-bold">{r.successPct == null ? "—" : `${r.successPct}%`}</p>
                  <p className="text-[11px] opacity-80">{r.success} ok &middot; {r.failed} failed{r.pending > 0 && ` · ${r.pending} pending`}</p>
                </div>
              ))}
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border max-h-96 overflow-y-auto">
              {data.recentAttempts.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">No STK attempts in this window.</p>
              ) : data.recentAttempts.map(a => (
                <div key={a.id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`stk-attempt-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      <span className="uppercase text-[10px] bg-secondary px-1.5 py-0.5 rounded mr-1">{a.rail}</span>
                      {a.purpose === "wallet_topup" ? "Wallet top-up" : a.purpose === "multi_contribution" ? "Bulk arrears" : "Contribution"}
                      {a.amount && <span className="ml-1 text-muted-foreground">· KES {Number(a.amount).toLocaleString()}</span>}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">{a.phone} &middot; {a.createdAt && format(new Date(a.createdAt), "dd MMM HH:mm")}</p>
                    {a.resultDesc && <p className="text-[10px] text-muted-foreground/80 italic truncate">{a.resultDesc}</p>}
                  </div>
                  <span className={`text-[10px] uppercase font-bold whitespace-nowrap ${a.status === "success" ? "text-emerald-700" : a.status === "failed" ? "text-red-700" : "text-amber-700"}`}>
                    {a.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
